import { useState } from 'react';

interface Template {
  id: string;
  name: string;
  category: 'invoice' | 'bid' | 'contract' | 'email';
  description: string;
  lastModified: string;
  usageCount: number;
}

const MOCK_TEMPLATES: Template[] = [
  { id: '1', name: 'Standard Video Invoice', category: 'invoice', description: 'Clean invoice template for video production services with line items and tax calculation.', lastModified: '2026-02-20', usageCount: 24 },
  { id: '2', name: 'Wedding Package Bid', category: 'bid', description: 'Comprehensive wedding videography bid with package tiers and add-ons.', lastModified: '2026-02-18', usageCount: 8 },
  { id: '3', name: 'Freelance Service Agreement', category: 'contract', description: 'Standard contract for freelance video production services with deliverables and payment terms.', lastModified: '2026-01-30', usageCount: 15 },
  { id: '4', name: 'Project Follow-Up', category: 'email', description: 'Email template for following up after an initial client consultation.', lastModified: '2026-02-12', usageCount: 32 },
  { id: '5', name: 'Corporate Video Bid', category: 'bid', description: 'Professional bid template for corporate and commercial video projects.', lastModified: '2026-02-05', usageCount: 11 },
  { id: '6', name: 'Payment Reminder', category: 'email', description: 'Polite payment reminder email for overdue invoices.', lastModified: '2026-01-22', usageCount: 19 },
];

function categoryColor(category: Template['category']): { bg: string; fg: string } {
  switch (category) {
    case 'invoice': return { bg: 'rgba(34,197,94,0.15)', fg: 'var(--color-success)' };
    case 'bid': return { bg: 'rgba(99,102,241,0.15)', fg: 'var(--color-primary)' };
    case 'contract': return { bg: 'rgba(245,158,11,0.15)', fg: 'var(--color-warning)' };
    case 'email': return { bg: 'rgba(59,130,246,0.15)', fg: 'var(--color-info)' };
  }
}

export function TemplatesPage() {
  const [categoryFilter, setCategoryFilter] = useState<'all' | Template['category']>('all');

  const filteredTemplates = MOCK_TEMPLATES.filter(
    (t) => categoryFilter === 'all' || t.category === categoryFilter,
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '4px' }}>Templates</h1>
          <p style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>
            Reusable templates for invoices, bids, contracts, and emails.
          </p>
        </div>
        <button
          style={{
            padding: '10px 20px',
            background: 'var(--color-primary)',
            color: '#fff',
            border: 'none',
            borderRadius: 'var(--radius-md)',
            fontWeight: 500,
            fontSize: '0.875rem',
          }}
        >
          + New Template
        </button>
      </div>

      {/* Category filter */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', flexWrap: 'wrap' }}>
        {(['all', 'invoice', 'bid', 'contract', 'email'] as const).map((cat) => (
          <button
            key={cat}
            onClick={() => setCategoryFilter(cat)}
            style={{
              padding: '8px 16px',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--color-border)',
              background: categoryFilter === cat ? 'var(--color-primary)' : 'transparent',
              color: categoryFilter === cat ? '#fff' : 'var(--color-text-muted)',
              fontSize: '0.8125rem',
              fontWeight: 500,
              textTransform: 'capitalize',
              cursor: 'pointer',
            }}
          >
            {cat === 'all' ? 'All' : `${cat}s`}
          </button>
        ))}
      </div>

      {/* Template gallery grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
          gap: '16px',
        }}
      >
        {filteredTemplates.map((template) => {
          const cc = categoryColor(template.category);
          return (
            <div
              key={template.id}
              style={{
                background: 'var(--color-surface)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-lg)',
                padding: '24px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                cursor: 'pointer',
                transition: 'border-color 0.15s',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <h3 style={{ fontSize: '1rem', fontWeight: 600, flex: 1 }}>
                  {template.name}
                </h3>
                <span
                  style={{
                    padding: '3px 10px',
                    borderRadius: '999px',
                    fontSize: '0.6875rem',
                    fontWeight: 500,
                    textTransform: 'capitalize',
                    background: cc.bg,
                    color: cc.fg,
                    flexShrink: 0,
                  }}
                >
                  {template.category}
                </span>
              </div>
              <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)', lineHeight: 1.5 }}>
                {template.description}
              </p>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: 'var(--color-text-muted)', marginTop: 'auto' }}>
                <span>Used {template.usageCount} times</span>
                <span>Modified {template.lastModified}</span>
              </div>
            </div>
          );
        })}
      </div>

      {filteredTemplates.length === 0 && (
        <div
          style={{
            padding: '48px',
            textAlign: 'center',
            color: 'var(--color-text-muted)',
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-lg)',
          }}
        >
          No templates in this category yet.
        </div>
      )}
    </div>
  );
}
